/** The small browser protocol carried inside the existing Mirror WebSocket transport. */
export interface WorldPosition { x: number; y: number; z: number }
export interface SpatialPlayer {
  userId: string;
  displayName: string;
  badgeUrl: string;
  position: WorldPosition;
  yaw: number;
  agoraUid: number;
  acousticRoom?: number;
  voiceDistances?: { audibleDistance: number; conversationalDistance: number } | null;
  level: number;
}
export interface SpatialSnapshot { selfId: string; players: SpatialPlayer[] }
export interface SpatialMap { vertices: WorldPosition[]; indices: number[] }

export function messageId(name: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < name.length; i++) hash = Math.imul(hash ^ (name.charCodeAt(i) & 255), 0x1000193);
  return ((hash >> 16) ^ hash) & 65535;
}
export const REQUEST_ID = messageId('FutureFest.Networking.LevelOneRequest');
export const RESPONSE_ID = messageId('FutureFest.Networking.LevelOneResponse');

// Mirror uses SQLite4 varints for batch message lengths, not protobuf varints.
function lengthBytes(n: number): number[] {
  if (n <= 240) return [n];
  if (n <= 2287) return [241 + ((n - 240) >> 8), (n - 240) & 255];
  if (n <= 67823) return [249, (n - 2288) >> 8, (n - 2288) & 255];
  throw new Error('Level 1 message is too large');
}

export function encodeRequest(payload: unknown): ArrayBuffer {
  const text = new TextEncoder().encode(JSON.stringify(payload));
  if (text.length > 16000) throw new Error('Level 1 request is too large');
  const length = lengthBytes(text.length + 4);
  const bytes = new Uint8Array(8 + length.length + 4 + text.length);
  const view = new DataView(bytes.buffer);
  view.setFloat64(0, performance.now() / 1000, true);
  bytes.set(length, 8);
  const offset = 8 + length.length;
  view.setUint16(offset, REQUEST_ID, true);
  view.setUint16(offset + 2, text.length + 1, true);
  bytes.set(text, offset + 4);
  return bytes.buffer;
}

export function decodeResponses(buffer: ArrayBuffer): unknown[] {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  const messages: unknown[] = [];
  let offset = 8; // batch timestamp
  const read = () => {
    if (offset >= bytes.length) throw new Error('Truncated multiplayer message');
    return bytes[offset++];
  };
  while (offset < bytes.length) {
    const a = read();
    let size: number;
    if (a <= 240) size = a;
    else if (a <= 248) size = 240 + (a - 241) * 256 + read();
    else if (a === 249) size = 2288 + read() * 256 + read();
    else if (a <= 251) {
      size = 0;
      for (let i = 0; i < a - 247; i++) size += read() * 256 ** i;
    } else throw new Error('Invalid multiplayer message length');
    const end = offset + size;
    if (size < 2 || end > bytes.length) throw new Error('Truncated multiplayer message');
    if (view.getUint16(offset, true) === RESPONSE_ID) {
      if (size < 4) throw new Error('Missing Level 1 payload');
      const textLength = view.getUint16(offset + 2, true) - 1;
      if (textLength < 0 || textLength !== size - 4) throw new Error('Invalid Level 1 payload');
      messages.push(JSON.parse(new TextDecoder().decode(bytes.subarray(offset + 4, end))));
    }
    // Native spawn/sync messages belong to Unity; the browser consumes only snapshots.
    offset = end;
  }
  return messages;
}
